import { toast } from '@devizy/ui';
import { CheckoutEventNames, initializePaddle } from '@paddle/paddle-js';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useLocale, useTranslations } from 'use-intl';

import { publicEnv } from '@/lib/env';
import { getPendingCheckoutTransactionId } from '@/lib/paddle-transaction';

const CHECKOUT_SETTINGS = {
  theme: 'light',
  variant: 'one-page',
  showAddDiscounts: false,
} as const;

export function PaddleLoader(): null {
  const t = useTranslations('paddle');
  const locale = useLocale();
  const queryClient = useQueryClient();
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (initialized.current || !publicEnv.paddleClientToken) return;
    initialized.current = true;
    initializePaddle({
      environment: publicEnv.paddleEnvironment,
      token: publicEnv.paddleClientToken,
      checkout: { settings: { ...CHECKOUT_SETTINGS, locale } },
      eventCallback: (event) => {
        if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
          toast.success(t('checkoutCompleted'), { description: t('checkoutCompletedHint') });
          void queryClient.invalidateQueries();
        }
      },
    })
      .then((paddle) => {
        const pendingTransactionId = getPendingCheckoutTransactionId();
        const transactionStillInUrl = new URLSearchParams(window.location.search).has('_ptxn');
        if (paddle && pendingTransactionId && !transactionStillInUrl) {
          paddle.Checkout.open({
            transactionId: pendingTransactionId,
            settings: { ...CHECKOUT_SETTINGS, locale },
          });
        }
      })
      .catch(() => {
        initialized.current = false;
      });
  }, [locale, queryClient, t]);

  return null;
}
